import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { THEME } from '../constants/theme';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectUri = searchParams.get('redirect_uri');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUri 
          ? `${window.location.origin}/reset-password?redirect_uri=${encodeURIComponent(redirectUri)}`
          : `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white relative overflow-hidden">
      {/* Background Circles */}
      <div className="absolute left-0 bottom-0 w-96 h-96 bg-[#E5F5F5] rounded-full -translate-x-1/2 translate-y-1/2" />
      <div className="absolute right-0 top-0 w-96 h-96 bg-[#E5F5F5] rounded-full translate-x-1/2 -translate-y-1/2" />
      <div className="absolute right-0 top-0 w-16 h-16 text-[#E5F5F5]">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.0001 1H13.0001V4H11.0001V1ZM15.0001 2H17.0001V5H15.0001V2ZM7.00006 3H9.00006V6H7.00006V3ZM20.0001 4H22.0001V7H20.0001V4ZM3.00006 5H5.00006V8H3.00006V5Z" />
        </svg>
      </div>

      <div className="w-full max-w-md px-6 relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <img src="/logo.png" alt="Aided" className="h-12" />
        </div>

        {/* Forgot Password Form */}
        <div className="bg-[#004D4D] rounded-2xl p-8 shadow-lg">
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-full p-4">
              <svg className="w-8 h-8 text-[#004D4D]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h2c0-1.66 1.34-3 3-3s3 1.34 3 3v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z" />
              </svg>
            </div>
          </div>

          {success ? (
            <div className="text-center">
              <h2 className="text-white text-xl font-medium mb-4">Check Your Email</h2>
              <p className="text-[#99CCCC] mb-6">
                We've sent you instructions to reset your password.
              </p>
              <Link
                to={redirectUri ? `/login?redirect_uri=${encodeURIComponent(redirectUri)}` : "/login"}
                className="inline-block w-full bg-[#66B2B2] hover:bg-[#99CCCC] text-white rounded-lg px-4 py-3 font-medium text-center transition-colors"
              >
                Back to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full bg-[#006666] text-white placeholder-[#99CCCC] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#99CCCC]"
                  required
                />
              </div>

              {error && (
                <div className="text-red-400 text-sm text-center">
                  {error}
                </div>
              )}

              <div className="flex justify-between items-center text-sm">
                <Link 
                  to={redirectUri ? `/login?redirect_uri=${encodeURIComponent(redirectUri)}` : "/login"} 
                  className="text-[#99CCCC] hover:text-white transition-colors"
                >
                  Back to Login
                </Link>
                <Link 
                  to={redirectUri ? `/signup?redirect_uri=${encodeURIComponent(redirectUri)}` : "/signup"} 
                  className="text-[#99CCCC] hover:text-white transition-colors"
                >
                  Sign Up
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#66B2B2] hover:bg-[#99CCCC] text-white rounded-lg px-4 py-3 font-medium transition-colors relative"
              >
                {loading ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
} 